// Contexto del chat de soporte (admin <-> usuario).
//
// Antes el estado del modal vivía DENTRO de TelegramModal, así que el botón
// flotante era la única forma de abrirlo y no había forma de enterarse de que
// había mensajes en cola sin abrir el panel.
//
// Ahora el estado es compartido:
//  - el botón flotante y el item del menú Admin abren el MISMO panel
//  - el contador de la cola vive acá, para que el badge del menú y el del
//    botón floten siempre con el mismo número
//
// El contador se pide por HTTP a /api/chat/estado (para el admin es la cola
// de TODOS los usuarios) y se refresca por dos vías:
//  - socket ('new-message' / 'chat_cola'): lo que lo hace inmediato
//  - poll: el respaldo si se pierde un evento
//
// El admin se une a la sala 'admin_soporte' desde acá y no solo desde el
// panel: si se une únicamente al abrir el panel, el contador del navbar solo
// se actualiza por el poll y el admin se entera tarde de que le escribieron.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from './authContext';

const SALA_ADMIN_SOPORTE = 'admin_soporte';
// Sin socket no hay eventos: el contador se refresca cada minuto como red de
// seguridad. Con socket conectado, no se hace polling.
const INTERVALO_RESPALDO_MS = 60000;

const tokenDeStorage = () => {
	try {
		const raw = localStorage.getItem('token');
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		return null;
	}
};

// Valor por defecto: si un componente usa el hook fuera del provider no
// revienta (solo que el item del menú no se muestra, porque `disponible`
// es false).
const valorPorDefecto = {
	disponible: false,
	panelAbierto: false,
	abrirPanel: () => { },
	cerrarPanel: () => { },
	cola: 0,
	respuestasSinLeer: 0,
	cargarCola: () => { },
	esAdmin: false,
};

export const ChatSoporteContext = createContext(valorPorDefecto);

export const useChatSoporte = () => useContext(ChatSoporteContext);

export const ChatSoporteProvider = ({ children, socket }) => {
	const auth = useContext(AuthContext);
	const token = auth?.token || tokenDeStorage();
	const esAdmin = auth?.role === 'admin';

	const [panelAbierto, setPanelAbierto] = useState(false);
	const [cola, setCola] = useState(0);
	// Respuestas del admin que el usuario todavía no leyó. Para el admin es
	// el total de sus respuestas sin leer (en todos los usuarios); para el
	// usuario, las suyas. Es el badge que le dice al usuario "el soporte te
	// contestó": sin esto la respuesta se quedaba esperando en silencio.
	const [respuestasSinLeer, setRespuestasSinLeer] = useState(0);

	const cargarCola = useCallback(async () => {
		try {
			const response = await fetch('/api/chat/estado', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!response.ok) return;

			const data = await response.json();
			// Para el admin, 'cola' es la cola de TODOS los usuarios; para el
			// usuario, la suya (los mensajes que escribió y el admin no abrió).
			setCola(Number(data?.cola) || 0);
			setRespuestasSinLeer(Number(data?.respuestasSinLeer) || 0);
		} catch (error) {
			console.error('Error al cargar la cola del chat de soporte:', error);
		}
	}, [token]);

	useEffect(() => {
		cargarCola();
	}, [cargarCola]);

	// Respaldo SOLO cuando no hay socket: mientras está conectado, el contador
	// se mantiene con los eventos de abajo. Antes había un poll cada 15 s
	// siempre, aunque el socket estuviera sano.
	useEffect(() => {
		const timer = setInterval(() => {
			if (!socket?.connected) cargarCola();
		}, INTERVALO_RESPALDO_MS);
		return () => clearInterval(timer);
	}, [socket, cargarCola]);

	// El admin se une a la sala del panel de soporte: así recibe los mensajes
	// aunque tenga el chat cerrado.
	useEffect(() => {
		if (!socket || !esAdmin) return;
		socket.emit('joinRoom', SALA_ADMIN_SOPORTE);
	}, [socket, esAdmin]);

	// Refresco inmediato del contador cuando llega algo por el socket.
	//
	// Para el admin: llega un mensaje de un usuario, o abrió una conversación
	// (los mensajes pasan a 'leido' y la burbuja tiene que bajar en el momento,
	// no recién con el poll).
	// Para el usuario: llega la respuesta del admin, o cambió el estado de sus
	// mensajes (ya no están "en cola").
	useEffect(() => {
		if (!socket) return undefined;

		const refrescar = () => { cargarCola(); };

		socket.on('new-message', refrescar);
		socket.on('chat_cola', refrescar);
		socket.on('chat_mensajes_estado', refrescar);
		// Al conectar o reconectar se resincroniza una sola vez: los eventos
		// que pasaron mientras el socket estaba caído no se recuperan solos.
		socket.on('connect', refrescar);

		return () => {
			socket.off('new-message', refrescar);
			socket.off('chat_cola', refrescar);
			socket.off('chat_mensajes_estado', refrescar);
			socket.off('connect', refrescar);
		};
	}, [socket, cargarCola]);

	const abrirPanel = useCallback(() => setPanelAbierto(true), []);
	const cerrarPanel = useCallback(() => setPanelAbierto(false), []);

	const valor = useMemo(
		() => ({
			disponible: true,
			panelAbierto,
			abrirPanel,
			cerrarPanel,
			cola,
			respuestasSinLeer,
			cargarCola,
			esAdmin,
		}),
		[panelAbierto, abrirPanel, cerrarPanel, cola, respuestasSinLeer, cargarCola, esAdmin]
	);

	return (
		<ChatSoporteContext.Provider value={valor}>
			{children}
		</ChatSoporteContext.Provider>
	);
};
